/**
 * P04 — Bulk asset import dialog (CSV with code-based resolution).
 *
 * Three states (own internal state, not a wizard library):
 *   1. PICK     — drag-drop a .csv (UTF-8); link to download the header template
 *   2. PREVIEW  — POST ?dryRun=true; show totals + first N row errors
 *   3. SUCCESS  — POST without dryRun; show final created/skipped/errors
 *
 * Errors block the Commit button, matching the workflow-import UX. A non-zero
 * skipped_duplicate count is treated as informational (re-uploading a file is
 * deliberately idempotent).
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  XCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  useBulkImportAssetsV2,
  type AssetBulkImportResponse,
  type AssetBulkImportRowError,
} from '@/api/hooks/useAssets';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Phase = 'pick' | 'preview' | 'success';

export default function ImportAssetsCsvDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation('assets');

  const [phase, setPhase] = useState<Phase>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<AssetBulkImportResponse | null>(null);
  const [final, setFinal] = useState<AssetBulkImportResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const mutation = useBulkImportAssetsV2();

  const reset = useCallback(() => {
    setPhase('pick');
    setFile(null);
    setPreview(null);
    setFinal(null);
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handlePick = (f: File | null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast.error(t('bulkImport.errors.notCsv'));
      return;
    }
    setFile(f);
    setPreview(null);
    setFinal(null);
    setPhase('pick');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) handlePick(e.dataTransfer.files[0]);
  };

  const runPreview = async () => {
    if (!file) return;
    try {
      const result = await mutation.mutateAsync({ file, dryRun: true });
      setPreview(result);
      setPhase('preview');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message
          ?? (err as Error).message;
      toast.error(msg ?? t('bulkImport.errors.previewFailed'));
    }
  };

  const runCommit = async () => {
    if (!file) return;
    try {
      const result = await mutation.mutateAsync({ file, dryRun: false });
      setFinal(result);
      setPhase('success');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message
          ?? (err as Error).message;
      toast.error(msg ?? t('bulkImport.errors.commitFailed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('bulkImport.title')}</DialogTitle>
          <DialogDescription>{t('bulkImport.description')}</DialogDescription>
        </DialogHeader>

        {phase === 'pick' && (
          <>
            <div
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50',
              )}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <FileSpreadsheet className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                {file ? file.name : t('bulkImport.dropHint')}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
              />
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mr-2 h-4 w-4" />
                {t('bulkImport.pickFile')}
              </Button>
            </div>

            <a
              href="/api/v2/assets/bulk-import/template"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <Download className="h-3.5 w-3.5" />
              {t('bulkImport.downloadTemplate')}
            </a>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                {t('bulkImport.cancel')}
              </Button>
              <Button
                disabled={!file || mutation.isPending}
                onClick={runPreview}
              >
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('bulkImport.preview')}
              </Button>
            </DialogFooter>
          </>
        )}

        {phase === 'preview' && preview && (
          <>
            <TotalsRow totals={preview.totals} />
            <ErrorList
              errors={preview.errors}
              truncated={preview.errorsTruncated}
              emptyLabel={t('bulkImport.clean')}
            />

            <DialogFooter>
              <Button variant="outline" onClick={reset}>
                {t('bulkImport.tryAnother')}
              </Button>
              <Button
                disabled={
                  mutation.isPending
                  || preview.totals.errors > 0
                  || preview.totals.created === 0
                }
                onClick={runCommit}
              >
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('bulkImport.commit', { count: preview.totals.created })}
              </Button>
            </DialogFooter>
          </>
        )}

        {phase === 'success' && final && (
          <>
            <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
              <CheckCircle2 className="mt-0.5 h-5 w-5" />
              <div className="text-sm">
                <div className="font-medium">{t('bulkImport.successTitle')}</div>
                <div className="opacity-80">
                  {t('bulkImport.successDescription', {
                    created: final.totals.created,
                    skipped: final.totals.skippedDuplicate,
                  })}
                </div>
              </div>
            </div>

            {final.totals.errors > 0 && (
              <ErrorList
                errors={final.errors}
                truncated={final.errorsTruncated}
                emptyLabel=""
              />
            )}

            <DialogFooter>
              <Button variant="outline" onClick={reset}>
                {t('bulkImport.importAnother')}
              </Button>
              <Button onClick={() => handleClose(false)}>{t('bulkImport.close')}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TotalsRow({ totals }: { totals: AssetBulkImportResponse['totals'] }) {
  const { t } = useTranslation('assets');
  return (
    <div className="flex flex-wrap gap-2">
      <Badge variant="secondary">
        {t('bulkImport.totals.created', { count: totals.created })}
      </Badge>
      <Badge variant="secondary">
        {t('bulkImport.totals.skipped', { count: totals.skippedDuplicate })}
      </Badge>
      <Badge variant={totals.errors > 0 ? 'destructive' : 'secondary'}>
        {t('bulkImport.totals.errors', { count: totals.errors })}
      </Badge>
    </div>
  );
}

function ErrorList({
  errors,
  truncated,
  emptyLabel,
}: {
  errors: AssetBulkImportRowError[];
  truncated: boolean;
  emptyLabel: string;
}) {
  const { t } = useTranslation('assets');
  if (errors.length === 0) {
    return emptyLabel ? (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-100">
        <CheckCircle2 className="mr-2 inline h-4 w-4" />
        {emptyLabel}
      </div>
    ) : null;
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-destructive">
        <XCircle className="h-4 w-4" />
        {t('bulkImport.errorsHeading', { count: errors.length })}
      </div>
      <div className="max-h-60 overflow-y-auto rounded-md border border-destructive/20 bg-destructive/5">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-background/95 backdrop-blur">
            <tr className="text-left">
              <th className="px-3 py-1.5">{t('bulkImport.columns.line')}</th>
              <th className="px-3 py-1.5">{t('bulkImport.columns.assetId')}</th>
              <th className="px-3 py-1.5">{t('bulkImport.columns.code')}</th>
              <th className="px-3 py-1.5">{t('bulkImport.columns.message')}</th>
            </tr>
          </thead>
          <tbody>
            {errors.map((e, idx) => (
              <tr key={idx} className="border-t border-destructive/10">
                <td className="px-3 py-1.5 align-top tabular-nums">{e.line}</td>
                <td className="px-3 py-1.5 align-top font-mono text-xs">{e.assetId}</td>
                <td className="px-3 py-1.5 align-top">
                  <Badge variant="outline" className="border-destructive/40 text-destructive">
                    {e.code}
                  </Badge>
                </td>
                <td className="px-3 py-1.5 align-top">{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {truncated && (
        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5" />
          {t('bulkImport.truncated')}
        </div>
      )}
    </div>
  );
}
